import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { meService } from "@/entities/user/api/me";
import { queryClient } from "@/shared/lib/queryClient";
import { beginHandedOffSupportSession } from "@/shared/lib/supportSession";
import { receiveSupportHandoff } from "@/shared/lib/supportHandoff";
import { saveAuthData } from "@/shared/lib/token";
import { tenantHome } from "@/shared/lib/router/tenantPath";

/** Recebe a sessão do console, passa a usá-la e devolve o endereço da organização vista. */
async function acceptSupportHandoff(): Promise<string> {
    const session = await receiveSupportHandoff();
    beginHandedOffSupportSession({
        accessToken: session.accessToken,
        expiresInSeconds: session.expiresInSeconds,
        targetLabel: session.targetLabel,
        returnUrl: session.returnUrl,
    });
    try {
        // O token de suporte não traz papéis: os menus são os da pessoa vista.
        const access = await meService.getAccess();
        saveAuthData(access.permissions, access.roles);
    } catch {
        saveAuthData([], []);
    }
    return tenantHome(session.tenantSlug);
}

/**
 * Destino da sessão de suporte aberta no console: recebe o token por postMessage (ver
 * shared/lib/supportHandoff) e entra na organização vista.
 */
export default function SupportHandoffPage() {
    const { t } = useTranslation("common");
    // Passagem de uso único: sem retry nem refetch, o token chega uma vez.
    const handoff = useQuery({
        queryKey: ["support-handoff"],
        queryFn: acceptSupportHandoff,
        retry: false,
        staleTime: Infinity,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
    const destination = handoff.data;

    useEffect(() => {
        if (!destination) return;
        queryClient.clear();
        window.location.replace(destination);
    }, [destination]);

    return (
        <div className="flex min-h-full items-center justify-center p-6">
            <div className="flex max-w-sm flex-col items-center gap-3 text-center">
                <Eye className="h-6 w-6 text-dark_green dark:text-green_on_dark" aria-hidden />
                {handoff.isError ? (
                    <>
                        <p className="font-semibold text-zinc-900 dark:text-zinc-100">{t("supportSession.handoffFailed")}</p>
                        <p role="alert" className="text-sm text-dark_gray dark:text-zinc-400">{handoff.error.message}</p>
                    </>
                ) : (
                    <p className="text-sm text-dark_gray dark:text-zinc-400">{t("supportSession.handoffWaiting")}</p>
                )}
            </div>
        </div>
    );
}
